import {
  CameraImageResponse,
  PlanogramComplianceBox,
  PlanogramComplianceResponse,
  PogBBox,
  ProductRecognitionBBox,
  ProductRecognitionPolygon,
  ShapeType,
  VizSimCandidate,
} from '../types';

/**
 * Converts a bounding box shape [x, y, width, height] to [x1, y1, x2, y2] format
 */
function shapeToBbox(shape: [number, number, number, number]): [number, number, number, number] {
  const [x, y, width, height] = shape;
  return [x, y, x + width, y + height];
}

/**
 * Gets bounding box coordinates from a detection item
 */
function getDetectionBbox(
  item: ProductRecognitionBBox | ProductRecognitionPolygon
): [number, number, number, number] | null {
  if ('x1' in item && 'y1' in item && 'x2' in item && 'y2' in item) {
    return [item.x1, item.y1, item.x2, item.y2];
  }
  if (item.shape && Array.isArray(item.shape) && item.shape.length === 4) {
    return shapeToBbox(item.shape as [number, number, number, number]);
  }
  return null;
}

/**
 * Gets the center point of a bounding box
 */
function getBboxCenter(bbox: [number, number, number, number]): [number, number] {
  const [x1, y1, x2, y2] = bbox;
  return [(x1 + x2) / 2, (y1 + y2) / 2];
}

/**
 * Checks if a point is contained within a bounding box
 */
function isPointInBox(point: [number, number], bbox: [number, number, number, number]): boolean {
  const [px, py] = point;
  const [x1, y1, x2, y2] = bbox;
  return px >= x1 && px <= x2 && py >= y1 && py <= y2;
}

/**
 * Gets bounding box coordinates from a PlanogramComplianceBox
 */
function getComplianceBoxBbox(
  box: PlanogramComplianceBox
): [number, number, number, number] | null {
  if (box.x1 !== null && box.y1 !== null && box.x2 !== null && box.y2 !== null) {
    return [box.x1, box.y1, box.x2, box.y2];
  }
  if (box.shape && Array.isArray(box.shape) && box.shape.length === 4) {
    return shapeToBbox(box.shape);
  }
  if (
    box.templateCoordinates &&
    Array.isArray(box.templateCoordinates) &&
    box.templateCoordinates.length === 4
  ) {
    return box.templateCoordinates as [number, number, number, number];
  }
  return null;
}

/**
 * Checks if the detection box is contained within (or equal to) the compliance box
 */
function isDetectionContainedInBox(
  detectionBbox: [number, number, number, number],
  boxBbox: [number, number, number, number]
): boolean {
  const [detX1, detY1, detX2, detY2] = detectionBbox;
  const [boxX1, boxY1, boxX2, boxY2] = boxBbox;

  // Detection is contained if all its corners are within or on the compliance box boundaries
  return detX1 >= boxX1 && detY1 >= boxY1 && detX2 <= boxX2 && detY2 <= boxY2;
}

/**
 * Finds the carton that contains the center point of a detection
 */
function findContainingCarton(
  detection: ProductRecognitionBBox | ProductRecognitionPolygon,
  cartonBoxes: ProductRecognitionBBox[] | null | undefined
): ProductRecognitionBBox | null {
  if (!cartonBoxes || cartonBoxes.length === 0) {
    return null;
  }

  const detectionBbox = getDetectionBbox(detection);
  if (!detectionBbox) {
    return null;
  }

  const center = getBboxCenter(detectionBbox);

  for (const carton of cartonBoxes) {
    const cartonBbox = getDetectionBbox(carton);
    if (!cartonBbox) continue;

    if (isPointInBox(center, cartonBbox)) {
      return carton;
    }
  }

  return null;
}

/**
 * Finds the PlanogramComplianceBox that contains the detection box
 */
function findMatchingComplianceBox(
  detection: ProductRecognitionBBox | ProductRecognitionPolygon,
  planogramComplianceData: PlanogramComplianceResponse | null | undefined,
  cartonBoxes: ProductRecognitionBBox[] | null | undefined
): PlanogramComplianceBox | null {
  if (!planogramComplianceData || !planogramComplianceData.boxes) {
    return null;
  }

  // Determine which detection to use for matching
  let detectionToMatch: ProductRecognitionBBox | ProductRecognitionPolygon = detection;

  // If detection is not a carton, find the containing carton first
  if (detection.type !== ShapeType.CARTON) {
    const containingCarton = findContainingCarton(detection, cartonBoxes);
    if (containingCarton) {
      detectionToMatch = containingCarton;
    }
    // If no containing carton found, fall back to using the original detection
  }

  const detectionBbox = getDetectionBbox(detectionToMatch);
  if (!detectionBbox) {
    return null;
  }

  for (const box of planogramComplianceData.boxes) {
    const boxBbox = getComplianceBoxBbox(box);
    if (!boxBbox) continue;

    if (isDetectionContainedInBox(detectionBbox, boxBbox)) {
      return box;
    }
  }

  return null;
}

/**
 * Finds the PogBBox that matches a pogItemId
 */
function findPogBBox(
  complianceBox: PlanogramComplianceBox,
  cameraImage: CameraImageResponse | null | undefined
): PogBBox | null {
  if (!cameraImage || !cameraImage.pogs) {
    return null;
  }

  const targetShelf = complianceBox.rogPositionVector.shelf;

  for (const pog of Object.values(cameraImage.pogs)) {
    if (!pog || !pog.bboxes) continue;

    // HACK: POG Compliance ignores top stock shelfs so if shelf 1 has only one
    // item and it is unstructured, skip it: shelf 2 becomes 1, 3 becomes 2, etc.
    const shelf1Boxes = pog.bboxes.filter((box) => box.shelf === 1);
    const skipFirstShelf = shelf1Boxes.length === 1 && shelf1Boxes[0].isUnstructured === true;
    const shelfToMatch = skipFirstShelf ? targetShelf + 1 : targetShelf;

    const matchingBox = pog.bboxes.find((box) => {
      return (
        box.column === complianceBox.rogPositionVector.position &&
        box.tagName === complianceBox.rogPositionVector.tagName &&
        box.shelf === shelfToMatch
      );
    });

    if (matchingBox) {
      if (matchingBox.isUnstructured) {
        return null;
      }

      return matchingBox;
    }
  }

  return null;
}

/**
 * Converts a PogBBox to a VizSimCandidate format
 */
function pogBBoxToCandidate(pogBox: PogBBox): VizSimCandidate {
  return {
    normalizedBarcode: pogBox.upc || '',
    similarity: null,
    isConfident: false,
    productName: pogBox.name || undefined,
    itemNumber: pogBox.itemNumber || undefined,
    productImageUri: pogBox.imageUri || pogBox.pogImageUri || undefined,
    productId: pogBox.productId,
    isExpectedPog: true,
    isActive: true,
  };
}

/**
 * Finds the expected POG item for a detection and converts it to a candidate format
 * For non-carton detections, finds the containing carton first, then looks up the compliance box
 */
export function findExpectedPogCandidate(
  detection: ProductRecognitionBBox | ProductRecognitionPolygon,
  planogramComplianceData: PlanogramComplianceResponse | null | undefined,
  cameraImage: CameraImageResponse | null | undefined,
  cartonBoxes?: ProductRecognitionBBox[] | null
): VizSimCandidate | null {
  // Step 1: Find matching PlanogramComplianceBox by bounding box overlap
  // For non-carton detections, this will first find the containing carton
  const matchingBox = findMatchingComplianceBox(detection, planogramComplianceData, cartonBoxes);
  if (!matchingBox || !matchingBox.pogItemId) {
    return null;
  }

  // Step 2: Find PogBBox that matches the pogItemId
  const pogBox = findPogBBox(matchingBox, cameraImage);
  if (!pogBox) {
    return null;
  }

  // Step 3: Convert PogBBox to candidate format
  return pogBBoxToCandidate(pogBox);
}
